// ProfileDialog.js
import { useState } from "react"
import { Typography } from '@mui/material'
import { ProfileChartjs } from './ProfileChartjs'
import "../../css/DemoCode.css"

/**
 * Frame for data inputs. 
 * 
 * @param {JSON} model A SWMM model in JSON format. In most cases this
 * is a 'submodel' containing only a copy of the affected sections. Using a copy allows
 * for continuous react-style editing without forcing the model to run on every keystroke.
 * @param {string} targetType The name of the SWMM inp section targetted by this form.
 * @param {string} target The name of the object targetted by this form.
 * @param {function} setNewName Sets the new name of this object if the user modifies the name.
 * @param {JSON} fullModel The 'full' SWMM model that contains supporting data for this component.
 * @param {function} setFullModel Sets the full model in case of any updates.
 * @returns UI for interactive swmm modeling.
 */
export default function ProfileDialog({ model, targetType, target, setNewName, fullModel, setFullModel }) {
  const [newTargetName, setNewTargetName] = useState(target)  
  

  function handleChange(e) {
    setNewName(e.target.value)
    setNewTargetName(e.target.value)
  }

  class Graph {
    constructor() {
      this.adjList = new Map();
    }
  
    addNode(node) {
      this.adjList.set(node, []);
    }
  
    addEdge(node1, node2, edgeName) {
      this.adjList.get(node1).push({ node: node2, name: edgeName });
      this.adjList.get(node2).push({ node: node1, name: edgeName });
    }
  
    findAllPaths(startNode, endNode) {
      const visited = new Set();
      const paths = [];
      this.dfs(startNode, endNode, visited, [startNode], paths);
      return paths;
    }
  
    dfs(currentNode, endNode, visited, path, paths) {
      visited.add(currentNode);
  
      if (currentNode === endNode) {
        paths.push([...path]);
      } else {
        const neighbors = this.adjList.get(currentNode);
        for (const neighbor of neighbors) {
          if (!visited.has(neighbor.node)) {
            path.push(neighbor.name);
            path.push(neighbor.node);
            this.dfs(neighbor.node, endNode, visited, path, paths);
            path.pop();
            path.pop();
          }
        }
      }
  
      visited.delete(currentNode);
    }
  }
  
  // Example usage:
  const graph = new Graph();
  graph.addNode(1);
  graph.addNode(2);
  graph.addNode(3);
  graph.addNode(4);
  graph.addNode(5);
  graph.addEdge(1, 2, 'Edge A');
  graph.addEdge(2, 3, 'Edge B');
  graph.addEdge(3, 4, 'Edge C');
  graph.addEdge(4, 5, 'Edge D');
  graph.addEdge(1, 5, 'Edge E');
  
  //const paths = graph.findAllPaths(startNode, endNode)
  

  // To recreate the profileData trace data:
  // 1: User selects a start node and an end node.
  // 2: The data structure is made:
  /**
   * {
   *   nodes: {},
   *   links: {}
   * }
   * 
   */
  // 3: A trace of the system is made.
  // 4: If there is no valid trace, throw error and return.
  // 4.5: If there is not valid output file, throw error and return.
  // 5: Insert traced nodes into the node section.
  // 6: Insert traced links into the links section.
  // A valid point in time is determined.
  // 7: Using traced node names, retrieve the 'depth' value for each node at the given time point.
  // 8: Insert the 'depth' values into the nodes.
  // 
  let profileData = {
    offsetEl: true,
    nodes: {
      node1: {
        depth: 5,
        maxDepth: 10,
        invertEl: 100,
      },
      node2: {
        depth: 2,
        maxDepth: 11,
        invertEl: 86,
      },
      node3: {
        depth: 3,
        maxDepth: 15,
        invertEl: 85,
      },
    },
    links: {
      pipe1: {
        inOffset: .5,
        maxDepth: 2,
        outOffset: 5,
        length: 50
      },
      pipe2: {
        inOffset: 3,
        maxDepth: 2,
        outOffset: 0.75,
        length: 100
      },
    },
    trace: [
      ['node1', 'pipe1', 'node2', 'pipe2', 'node3']
    ]
  }

  // Just get the bottom elevations and points,
  // and the ground elevations
  const parseOutputData = (p) => {
    let dist = 0.0
    let elev = 0.0
    let groundElevs = []
    let bottomElevs = []
    let waterElevs  = []
    let nodeShape = {}

    p.trace[0].forEach((v, i) => {
      // If this is a node (even entry in trace)
      if( i % 2 === 0){
        elev = p.nodes[v].invertEl
        // Create manholes
        nodeShape[v] = []
        nodeShape[v].push({DateTime: dist,   Value: p.nodes[v].invertEl})
        nodeShape[v].push({DateTime: dist,   Value: p.nodes[v].invertEl + p.nodes[v].maxDepth})
        nodeShape[v].push({DateTime: dist+3, Value: p.nodes[v].invertEl + p.nodes[v].maxDepth})
        nodeShape[v].push({DateTime: dist+3, Value: p.nodes[v].invertEl})
        nodeShape[v].push({DateTime: dist,   Value: p.nodes[v].invertEl})

        waterElevs.push({DateTime: dist, Value: elev + p.nodes[v].depth})
        bottomElevs.push({DateTime: dist, Value: elev})
        dist = dist + 3
        waterElevs.push({DateTime: dist, Value: elev + p.nodes[v].depth})
        bottomElevs.push({DateTime: dist, Value: elev})
      }// If this is a link (odd entry in trace)
      if( i % 2 === 1){
        // Create pipes
        nodeShape[v] = []
        nodeShape[v].push({DateTime: dist,   Value: elev + p.links[v].inOffset})
        waterElevs.push({DateTime: dist,   Value: elev + p.nodes[p.trace[0][i-1]].depth})
        nodeShape[v].push({DateTime: dist,   Value: elev + p.links[v].inOffset + p.links[v].maxDepth})
        nodeShape[v].push({DateTime: dist + p.links[v].length, Value: p.nodes[p.trace[0][i+1]].invertEl + p.links[v].outOffset + p.links[v].maxDepth})
        nodeShape[v].push({DateTime: dist + p.links[v].length, Value: p.nodes[p.trace[0][i+1]].invertEl + p.links[v].outOffset })
        nodeShape[v].push({DateTime: dist,   Value: elev + p.links[v].inOffset})
        // If the downstream node depth is lower than the exit elevation of the link, then the downstream depth is the exit elevation of the link
        if(p.nodes[p.trace[0][i+1]].invertEl + p.links[v].outOffset > p.nodes[p.trace[0][i+1]].invertEl + p.nodes[p.trace[0][i+1]].depth){
          waterElevs.push({DateTime: dist + p.links[v].length, Value: p.nodes[p.trace[0][i+1]].invertEl + p.links[v].outOffset }) // Value: p.nodes[p.trace[0][i+1]].invertEl + p.links[v].outOffset
          // Otherwise, the downstream depth is the elevation of the water in the downstream node
        } else {
          waterElevs.push({DateTime: dist + p.links[v].length, Value: p.nodes[p.trace[0][i+1]].invertEl + p.nodes[p.trace[0][i+1]].depth })
        }

        // If the downstream node elevations are lower than the downstream pipe invert,
        // then the downstream node elevation is the same as the pipe downstream elevation.
        elev = elev + p.links[v].inOffset
        bottomElevs.push({DateTime: dist, Value: elev})
        dist = dist + p.links[v].length
        elev = p.nodes[p.trace[0][i+1]].invertEl
        elev = elev + p.links[v].outOffset
        bottomElevs.push({DateTime: dist, Value: elev})
      }
    })
    
    dist = 0.0
    elev = 0.0
    // Create the ground elevations
    p.trace[0].forEach((v, i) => {
      if( i % 2 === 0){
        elev = p.nodes[v].invertEl + p.nodes[v].maxDepth
        groundElevs.push({DateTime: dist, Value: elev})
        dist = dist + 3
        groundElevs.push({DateTime: dist, Value: elev})
      }// If this is a link (odd entry in trace)
      if( i % 2 === 1){
        groundElevs.push({DateTime: dist, Value: elev})
        dist = dist + p.links[v].length
        elev = p.nodes[p.trace[0][i+1]].invertEl + p.nodes[p.trace[0][i+1]].maxDepth
        groundElevs.push({DateTime: dist, Value: elev})
      }
    })

    return {grnd: groundElevs, bott: bottomElevs, water: waterElevs, ...nodeShape}
  }

  // Find the node output entries that are in this trace.
  // Find the records that match this point in time.
  // Inject the records into the node data, like
  let outData = parseOutputData(profileData)

  /**
   * If there is a valid model, create a UI for the model.
   */
  if(model){
    return(
      <>
      {
        <div style={{ flex:1, height:'100%', backgroundColor:'black'}} >
          <Typography id="modal-modal-title" variant="p4" >
            <div style={{display:"flex", flexFlow: "column"}}>
              <label>Profile</label>
              <input style={{fontSize: '16px', background: 'rgb(50, 50, 50)', color: 'lightskyblue', border: 'none', textAlign: 'center'}} type="text" value={newTargetName} onChange={handleChange} />
            </div>
            <div style={{display:"flex", minHeight: '80px', height: 'calc(100% - 80px)'}}>
              <ProfileChartjs 
                out_data={outData} 
                model={fullModel}
                targets={['water', 'grnd', 'bott', 'node1', 'node2', 'node3', 'pipe1', 'pipe2']} 
                titleText={`Profile Data`} />
            </div>
          </Typography>
        </div>
        }
      </>
    )
  } else {
    return null
  }
}